// src/pages/admin/SecurityPage.jsx
import { useState, useEffect, useCallback } from "react";
import { api } from "../../services/api";

const COLORS = {
  bg: "#F4F5F7",
  card: "#ffffff",
  dark: "#0c1118",
  amber: "#111111",
  border: "#E8EAED",
  text: "#374151",
  muted: "#6B7280",
  danger: "#ef4444",
  success: "#16a34a",
};

const S = {
  page: { fontFamily: "'DM Sans', sans-serif" },
  header: { marginBottom: 28 },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: COLORS.dark, fontFamily: "'DM Sans', sans-serif" },
  subtitle: { margin: "4px 0 0", fontSize: 14, color: COLORS.muted },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 28 },
  statCard: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "20px 24px" },
  statLabel: { fontSize: 12, fontWeight: 600, color: COLORS.muted, letterSpacing: 0.5, margin: 0 },
  statValue: { fontSize: 32, fontWeight: 700, color: COLORS.dark, margin: "6px 0 0" },
  card: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 24 },
  cardHeader: { background: COLORS.dark, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  cardTitle: { margin: 0, fontSize: 13, fontWeight: 700, color: "#ffffff", letterSpacing: 0.5 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "11px 16px", fontSize: 12, fontWeight: 600, color: COLORS.muted, textAlign: "left", borderBottom: `1px solid ${COLORS.border}`, background: "#F8F9FA" },
  td: { padding: "12px 16px", fontSize: 13, color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` },
  badge: (color, bg) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 700, color, background: bg }),
  filters: { display: "flex", gap: 10, padding: "14px 20px", borderBottom: `1px solid ${COLORS.border}`, background: "#F8F9FA", flexWrap: "wrap" },
  select: { padding: "6px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, color: COLORS.dark, background: "#fff", cursor: "pointer" },
  empty: { padding: "40px 20px", textAlign: "center", color: COLORS.muted, fontSize: 14 },
};

function statusBadge(status) {
  const map = {
    success: ["#166534", "#DCFCE7"],
    failure: ["#991B1B", "#FEE2E2"],
    blocked: ["#92400E", "#FEF3C7"],
  };
  const [c, bg] = map[status] || [COLORS.muted, "#F3F4F6"];
  return <span style={S.badge(c, bg)}>{status}</span>;
}

function threatBadge(t) {
  if (!t) return null;
  return <span style={S.badge("#7C3AED", "#EDE9FE")}>{t.replace(/_/g, " ")}</span>;
}

export default function SecurityPage() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ category: "", status: "", flagged: "" });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get("/api/admin/security/stats");
      setStats(r.data || r);
    } catch (e) { console.error(e); }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filters.category) params.set("category", filters.category);
      if (filters.status)   params.set("status",   filters.status);
      if (filters.flagged)  params.set("flagged",  filters.flagged);
      const r = await api.get(`/api/admin/security/audit-logs?${params}`);
      const data = r.data || r;
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };
  const totalPages = Math.ceil(total / 50);

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>Security</h1>
        <p style={S.subtitle}>Audit logs, access violations, and flagged events — last 24 hours shown in stats.</p>
      </div>

      {/* Stats */}
      <div style={S.statsRow}>
        {[
          { label: "Total Events (24h)", val: stats?.total ?? "—" },
          { label: "Failures (24h)",     val: stats?.failures ?? "—", color: "#dc2626" },
          { label: "Flagged (24h)",      val: stats?.flagged ?? "—",  color: "#333333" },
          { label: "Blocked (24h)",      val: stats?.blocked ?? "—",  color: "#7C3AED" },
        ].map(({ label, val, color }) => (
          <div key={label} style={S.statCard}>
            <p style={S.statLabel}>{label}</p>
            <p style={{ ...S.statValue, color: color || COLORS.dark }}>{val}</p>
          </div>
        ))}
      </div>

      {/* Audit Log Table */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <span style={S.cardTitle}>Audit Log</span>
          <span style={{ fontSize: 12, color: COLORS.muted }}>{total.toLocaleString()} records</span>
        </div>
        <div style={S.filters}>
          <select style={S.select} value={filters.category} onChange={e => handleFilter("category", e.target.value)}>
            <option value="">All Categories</option>
            {["AUTH","ADMIN","USER","ORG","BILLING","QUOTE","SYSTEM"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select style={S.select} value={filters.status} onChange={e => handleFilter("status", e.target.value)}>
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="blocked">Blocked</option>
          </select>
          <select style={S.select} value={filters.flagged} onChange={e => handleFilter("flagged", e.target.value)}>
            <option value="">All Events</option>
            <option value="true">Flagged Only</option>
          </select>
        </div>

        {loading ? (
          <div style={S.empty}>Loading...</div>
        ) : logs.length === 0 ? (
          <div style={S.empty}>No audit log entries found.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Time","Category","Action","Status","User","IP","Threat"].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td style={S.td}>{new Date(log.at).toLocaleString()}</td>
                    <td style={S.td}><span style={S.badge(COLORS.dark, "#F3F4F6")}>{log.category}</span></td>
                    <td style={{ ...S.td, fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}>{log.action}</td>
                    <td style={S.td}>{statusBadge(log.status)}</td>
                    <td style={S.td}>{log.userEmail || log.user?.email || <span style={{ color: COLORS.muted }}>—</span>}</td>
                    <td style={{ ...S.td, fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}>{log.ip || "—"}</td>
                    <td style={S.td}>{threatBadge(log.threatType)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", gap: 8, padding: "14px 20px", justifyContent: "flex-end", borderTop: `1px solid ${COLORS.border}` }}>
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
              style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "#fff", cursor: page===1?"not-allowed":"pointer", fontSize: 13 }}>
              Previous
            </button>
            <span style={{ padding: "6px 14px", fontSize: 13, color: COLORS.muted }}>
              Page {page} of {totalPages}
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
              style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "#fff", cursor: page===totalPages?"not-allowed":"pointer", fontSize: 13 }}>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
